<template>
  <div class="ag-grid">
    <ag-grid-vue
      class="ag-theme-balham default-grid"
      :column-defs="columnDefs"
      :row-data="rowData"
      :pagination="true"
      :pagination-page-size="pageLimit"
      :grid-options="gridOptions"
      :key="gridKey"
      :frameworkComponents="frameworkComponents"
      :row-key="rowKey"
      :row-selection="'multiple'"
      :suppress-row-click-selection="true"
      :suppressPaginationPanel="true"
      @row-selected="handleSelectionChange"
    >
    </ag-grid-vue>
  </div>
</template>

<script>
import { formatLocale } from '@/utils/helpers/StringsNumbers'
import { AgGridVue } from 'ag-grid-vue'
import 'ag-grid-enterprise'
import AgGridColumnMethods from '@/Mixins/SigmaClientNew/AgGridColumnMethods'
import ButtonCellRenderer from './ButtonCellRenderer.vue'

export default {
  components: { AgGridVue },
  mixins: [AgGridColumnMethods],
  props: {
    completed: Boolean,
    activeTab: String,
    pageLimit: Number,
    paginatedData: Array,
    parseTimestamp: Function,
    tableReady: Boolean,
    handleSelectionChange: Function,
    showLedgerItem: Function,
    busy: Boolean
  },
  data () {
    return {
      gridKey: 0,
      columnDefs: [],
      rowData: [],
      gridApi: null,
      columnApi: null,
      scope: this,
      frameworkComponents: null,
      gridOptions: {
        treeData: true,
        undoRedoCellEditing: true,
        singleClickEdit: true,
        groupDefaultExpanded: 0,
        defaultColDef: {
          resizable: true,
          sortable: true,
          unSortIcon: true
        },
        aggFuncs: {
          sum: this.customSum
        },
        groupRemoveSingleChildren: true,
        autoGroupColumnDef: {
          headerName: 'Check # or Ledger ID',
          minWidth: 200,
          cellRendererParams: {
            suppressCount: true
          },
          checkboxSelection: !this.completed,
          headerCheckboxSelection: !this.completed,
          comparator: (ledgerId1, ledgerId2, c, d) => {
            let val1 = c.group ? c.allLeafChildren[0].data[c.allLeafChildren[0].data.type === 'checks' ? 'ledger' : 'ledger_id'] : c.data[c.data.type === 'checks' ? 'ledger' : 'ledger_id']
            let val2 = d.group ? d.allLeafChildren[0].data[c.allLeafChildren[0].data.type === 'checks' ? 'ledger' : 'ledger_id'] : d.data[c.data.type === 'checks' ? 'ledger' : 'ledger_id']
            return val1 - val2
          }
        },
        getDataPath: (data) => {
          let isMultiLine = false
          let matches = 0
          for (let row of this.rowData) {
            if (row.payment_confirmation_number === data.payment_confirmation_number && row.ledger_id === data.ledger_id) {
              matches++
            } else if (row.ledger_id === data.ledger_id && !row.payment_confirmation_number && !data.payment_confirmation_number) {
              matches++
            }
          }
          if (matches > 1) {
            isMultiLine = true
          }
          if (data.type && data.type.includes('ap') && !data.number && !isMultiLine) {
            return [`${data.transaction_id}`, `${data.ledger_id}`]
          } else if (data.type && data.type.includes('ap') && (!data.number || data.number === '')) {
            return [`${data.ledger_id}`, `line ${data.line_number}`]
          }
          let paymentTypes = ['EFT', 'ACH', 'MANUAL', 'WIRE', 'NA']
          if (data.payment_type) {
            if (paymentTypes.find(type => {
              return data.payment_type.toUpperCase().includes(type)
            })) {
              let formattedPaymentType = ''
              if (data.payment_type.includes('EFT') || data.payment_type.includes('ACH')) {
                formattedPaymentType = data.payment_type
                if (isMultiLine) {
                  return [`${data.number} - ${formattedPaymentType}`, `${data.ledger_id} - line ${data.line_number}`]
                } else {
                  return [`${data.number} - ${formattedPaymentType}`, `${data.ledger_id}`]
                }
              } else {
                formattedPaymentType = data.payment_type.replace('_', ' ').toLowerCase().replace(/\w\S*/g, (w) => (w.replace(/^\w/, (c) => c.toUpperCase())))
              }
              if (formattedPaymentType.toUpperCase().includes('MANUAL')) {
                let words = formattedPaymentType.split(' ')
                formattedPaymentType = `${words[1]} ${words[0]}`
              }
              if (data.number) {
                if (isMultiLine) {
                  return [`${data.number} - ${formattedPaymentType}`, `${data.ledger_id} - line ${data.line_number}`]
                } else {
                  return [`${data.number} - ${formattedPaymentType}`, `${data.ledger_id}`]
                }
              }
            }
          } else {
            if (isMultiLine) {
              return data.number ? [`${data.number}`] : [`${data.ledger_id} - line ${data.line_number}`]
            } else {
              return data.number ? [`${data.number}`] : [`${data.ledger_id}`]
            }
          }
        },
        onGridReady: params => {
          this.$data.scope.onGridReady(params)
        },
        onFirstDataRendered: params => {
          this.$data.scope.onFirstDataRendered(params)
        },
        onCellValueChanged: params => {
          this.$data.scope.onCellValueChanged(params)
        },
        getRowNodeId: data => {
          return data.id
        },
        rowClassRules: {
          'ag-changed': params => {
            return (params.data || {}).edited || false
          }
        }
      },
      ledgerIdColumn: {
        headerName: 'Ledger ID',
        field: 'ledger_id',
        width: 200,
        hide: true
      },
      checkColumn: {
        headerName: 'Check',
        field: 'number',
        aggFunc: 'first',
        width: 100,
        comparator: (check1, check2) => {
          if (!check1) check1 = ''
          if (!check2) check2 = ''
          if (check1 && check2) {
            return check1.localeCompare(check2, 'en', { numeric: true })
          }
          return check1.localeCompare(check2)
        }
      },
      timePostedColumn: {
        headerName: 'Posted on',
        field: 'transaction_date',
        aggFunc: 'first',
        width: 120,
        comparator: (date1, date2) => {
          let dateObj1 = new Date(date1)
          let dateObj2 = new Date(date2)
          return dateObj1.getTime() - dateObj2.getTime()
        }
      },
      descriptionColumn: {
        headerName: 'Description',
        field: 'meta_name',
        width: 300,
        aggFunc: function (data) {
          let valueString = data.values[0]
          if (valueString) {
            let words = valueString.split(' ')
            let returnString = ''
            for (let index in words) {
              if (index >= (words.length - 4)) {
                returnString = returnString + words[index] + ' '
              }
            }
            return returnString
          }
        },
        comparator: (metaName1, metaName2, c, d) => {
          let data1 = c.group ? c.allLeafChildren[0].data.meta_name : c.data.meta_name
          let data2 = d.group ? d.allLeafChildren[0].data.meta_name : d.data.meta_name
          return data1.localeCompare(data2, undefined, { numeric: true })
        }
      },
      amtColumn: {
        headerName: 'Amt',
        field: 'amount_decimal',
        aggFunc: 'sum',
        type: 'rightAligned',
        width: 150,
        comparator: (amt1, amt2) => {
          if (amt1 && amt2) {
            return amt1 - amt2
          }
          return amt1.localeCompare(amt2, undefined, { numeric: true })
        }
      },
      viewColumn: {
        headerName: 'View',
        sortable: false,
        unSortIcon: false,
        suppressMovable: true,
        cellRenderer: 'btnCellRenderer',
        cellRendererParams: {
          showLedgerItem: this.showLedgerItem
        }
      }
    }
  },
  methods: {
    /**
     * Formats dollar amounts with thousands separators
     * @param value
     * @return {*}
     */
    formatAmount (value) {
      return value ? formatLocale(value) : ''
    },
    onGridReady (params) {
      if (params) {
        this.gridApi = params.api
        this.columnApi = params.columnApi
        this.$emit('onNewGrid', params)
      }
    },
    onFirstDataRendered (params) {
      this.loaded = true
      this.gridApi.forEachNode(node => {
        node.setExpanded(false)
        if (node.allLeafChildren.length && node.allLeafChildren.every(child => child.selected)) {
          node.selected = true
        }
      })
      params.api.redrawRows()
    },
    onCellValueChanged (params) {
      // Check if any change occurred so we can update the corresponding resource
      if (params.value && params.oldValue === params.newValue) return

      const rowNode = this.gridApi.getDisplayedRowAtIndex(params.rowIndex)

      // Mark data as changed for css purposes
      params.data.edited = true
      this.redrawCurrentRow(rowNode)

      this.setCellFocus()
    }
  },
  watch: {
    paginatedData () {
      this.rowData = this.paginatedData
      this.gridKey++
    }
  },
  beforeMount () {
    this.frameworkComponents = {
      btnCellRenderer: ButtonCellRenderer
    }
  },
  mounted () {
    this.rowData = this.paginatedData
    this.columnDefs.push(this.ledgerIdColumn)
    this.columnDefs.push(this.checkColumn)
    this.columnDefs.push(this.timePostedColumn)
    this.columnDefs.push(this.descriptionColumn)
    this.columnDefs.push(this.amtColumn)
    this.columnDefs.push(this.viewColumn)
  }
}
</script>

<style>
@import '../../../../node_modules/ag-grid-community/dist/styles/ag-grid.css';
  @import '../../../../node_modules/ag-grid-community/dist/styles/ag-theme-balham.css';

  ag-grid {
    width: 100%;
    height: 540px;
    background: #ffffff;
    font-family: "Helvetica", Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-align: left;
  }
  .default-grid { float: left; width: 1150px; height: 800px; }
</style>



// WEBPACK FOOTER //
// src/components/Programs/BankRecon/BankRecTable.vue